import { useCallback, useState } from "react";

import { Alert } from "@vericus/cadmus-ui";

import { useAppSelector } from "@/data/hooks";
import { selectAnswerSnapshot } from "@/features/authority/authority-slice/selectors";
import { hasCompletedResponse } from "@/features/socratic/helpers/has-dialogue-response";
import { selectedDialogueIdFromSnapshot } from "@/features/socratic/helpers/selected-dialogue-id";
import { useRecordingGate } from "@/features/socratic/hooks";
import { CreateDialogueControls } from "@/features/socratic/hooks/use-create-dialogue";
import { useSelectVideoResponseAttempt } from "@/features/socratic/hooks/use-select-video-response-attempt";
import { selectReattemptWindowEndDatePassed } from "@/features/socratic/socratic-slice";
import {
  CreateSocraticDialogueError,
  SocraticDialogueFragment,
  SocraticSessionFragment,
} from "@/generated/graphql";
import { useRootNavigate } from "@/routes";

import { AttemptsList } from "./attempts-list";
import { ReattemptControls } from "./reattempt-controls";
import * as styles from "./socratic-session-entry.css";
import { StartDialogueCTA } from "./start-dialogue-cta";

interface Props {
  session: SocraticSessionFragment;
  answerBlockId: string;
  workId: string;
  isReadonly?: boolean;
  hasPreparationTime: boolean;
  numAttemptsRemaining: number;
  createDialogue: CreateDialogueControls;
}

export function SocraticAttemptActions(props: Props) {
  const {
    session,
    answerBlockId,
    workId,
    isReadonly,
    hasPreparationTime,
    numAttemptsRemaining,
  } = props;
  const rootNavigate = useRootNavigate();

  const [isNavigating, setIsNavigating] = useState(false);

  const { createDialogue, creatingDialogue, createError } =
    props.createDialogue;

  const handleStartOrReattemptPressed = () => {
    if (isReadonly === true) return;
    createDialogue({
      workId,
      session,
      onSuccess: () => {
        rootNavigate(`/work/${workId}/socratic/${answerBlockId}`);
        setIsNavigating(true);
      },
    });
  };

  const snapshot = useAppSelector((state) =>
    selectAnswerSnapshot(state, answerBlockId)
  );

  const reattemptWindowEndDatePassed = useAppSelector(
    selectReattemptWindowEndDatePassed
  );

  const { wouldAutoSubmitDuringRecording } = useRecordingGate();

  const selectedDialogueId = selectedDialogueIdFromSnapshot(snapshot);

  const selectAttempt = useSelectVideoResponseAttempt({
    answerBlock: session.answerBlock,
  });

  const handleSelectAttempt = useCallback(
    (dialogue: SocraticDialogueFragment) => {
      if (isReadonly === true) return;
      selectAttempt(dialogue);
    },
    [isReadonly, selectAttempt]
  );

  // The window can shut between the page loading and the press. Naming that is
  // worth a branch of its own: "Something went wrong" invites a reload, and a
  // reload will not reopen the window.
  if (createError === CreateSocraticDialogueError.OutsideReattemptWindow) {
    return (
      <Alert severity="error" size="sm">
        <div>Your re-attempt window has expired.</div>
        <div>Please contact your instructor if you have any questions.</div>
      </Alert>
    );
  }

  if (createError !== null) {
    return (
      <Alert title="Something went wrong">
        Please reload the page to try again.
      </Alert>
    );
  }

  const loading = creatingDialogue || isNavigating;

  // Only show the submitted UI once a response has actually completed uploading.
  // A dialogue can exist without a completed response (abandoned / uploading /
  // failed), in which case the start CTA should still be shown.
  const hasResponse = hasCompletedResponse(session.dialogues);

  const hasReattemptsRemaining =
    numAttemptsRemaining > 0 &&
    reattemptWindowEndDatePassed !== true &&
    !wouldAutoSubmitDuringRecording;

  return (
    <div className={styles.actions}>
      {!hasResponse ? (
        <StartDialogueCTA
          onStartPressed={handleStartOrReattemptPressed}
          loading={loading}
          isReadonly={isReadonly}
          numAttemptsRemaining={numAttemptsRemaining}
          hasPreparationTime={hasPreparationTime}
        />
      ) : (
        <AttemptsList
          dialogues={session.dialogues}
          selectedDialogueId={selectedDialogueId}
          onSelectAttempt={handleSelectAttempt}
          hasReattemptsRemaining={hasReattemptsRemaining}
          isReadonly={isReadonly}
          headerAction={
            <ReattemptControls
              onReattemptPressed={handleStartOrReattemptPressed}
              loading={loading}
              isReadonly={isReadonly}
              numAttemptsRemaining={numAttemptsRemaining}
            />
          }
        />
      )}
    </div>
  );
}
